import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { useFeature } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	Avatar,
	FlexGap,
	IconButton,
	Label,
	QuillText,
	type UserAvatarView,
	UserPlusIcon,
	tokens,
} from '@treams/treams-ui';

import NominateSuccessorModal from '../../../analytics/talent-review/NominateSuccessorModal';
import { readinessLabelKey } from '../../../analytics/talent-review/successorReadiness';
import { useJobTitleName } from '../../../job/useJobTitleName';
import { successorTooltipText } from './successorTooltipText';
import { TalentReviewTooltip } from './TalentReviewTooltip';

type Person = UserAvatarView & { id: string };

type Nomination = {
	id: string;
	jobTitleID: string;
	jobTitle: string;
	readiness: graphql.SuccessorReadiness;
	motivation: graphql.JobPositionSuccessorFragment['motivation'];
	viewerCanArchive: boolean;
	viewerCanNominate: boolean;
};

type SuccessorGroup = {
	user: Person;
	nominations: Nomination[];
};

const AVATAR_SIZE = 32;

const AvatarButton = styled.button`
	display: inline-flex;
	padding: 0;
	border: 0;
	background: none;
	border-radius: ${tokens.radius.full}px;
	cursor: pointer;
`;

const SuccessorTooltip = styled(TalentReviewTooltip)`
	display: inline-flex;
	line-height: 0;
`;

// Open the nomination this viewer can withdraw. Otherwise the first one for a
// title they may nominate for. A view-only viewer gets nothing to open.
function nominationToEdit(group: SuccessorGroup): Nomination | undefined {
	return (
		group.nominations.find((nomination) => nomination.viewerCanArchive) ??
		group.nominations.find((nomination) => nomination.viewerCanNominate)
	);
}

/**
 * Possible successors for the person's key role. Hidden unless they currently
 * hold a sleutelrol. The plus opens the nomination modal with that job title
 * filled in. An avatar opens the same modal for that successor, so the
 * nominator can edit the motivation and readiness or withdraw it. A viewer who
 * may only see the key role gets neither the plus nor a clickable avatar.
 * @param props component props
 * @param props.user the person whose sidebar card is open
 */
export default function TalentReviewSidebarSuccessors(props: { user: Person }) {
	const { t } = useTranslation();
	const jobTitleName = useJobTitleName();
	const enabled = useFeature(graphql.FeatureName.Succession);
	const [modal, setModal] = useState<{
		jobTitleID: string;
		nomination?: Nomination & { user: Person };
	} | null>(null);
	const { user } = props;

	const { data } = graphql.useJobTitleSuccessorsQuery({ skip: !enabled });

	const keyRoles = useMemo(() => {
		return (data?.jobTitleSuccessors ?? [])
			.filter(
				(entry) =>
					entry.jobTitle.isKeyPosition && entry.holders.some((holder) => holder.id === user.id),
			)
			.map((entry) => ({
				id: entry.jobTitle.id,
				title: jobTitleName(entry.jobTitle),
				canNominate: entry.jobTitle.viewerCanManageSuccession,
				successors: entry.successors,
			}))
			.sort((a, b) => a.title.localeCompare(b.title));
	}, [data, jobTitleName, user.id]);

	const successors = useMemo(() => {
		const byID = new Map<string, SuccessorGroup>();

		for (const role of keyRoles) {
			for (const successor of role.successors) {
				const nomination: Nomination = {
					id: successor.id,
					jobTitleID: role.id,
					jobTitle: role.title,
					readiness: successor.readiness,
					motivation: successor.motivation,
					viewerCanArchive: successor.viewerCanArchive,
					viewerCanNominate: role.canNominate,
				};
				const existing = byID.get(successor.user.id);

				if (existing) {
					existing.nominations.push(nomination);
				} else {
					byID.set(successor.user.id, { user: successor.user, nominations: [nomination] });
				}
			}
		}

		// Newest nomination is first in the query; show the latest on the right,
		// next to the add control.
		return [...byID.values()].reverse();
	}, [keyRoles]);

	if (!enabled || keyRoles.length === 0) {
		return null;
	}

	// A parent-department manager sees the key roles but may not nominate.
	const nominatableRole = keyRoles.find((role) => role.canNominate);

	const addLabel = t('analytics.talentReview.succession.add');
	const names = successors
		.map((group) => `${group.user.firstName} ${group.user.lastName}`)
		.join(', ');
	const label = names ? `${addLabel} (${names})` : addLabel;

	return (
		<Box mt={2}>
			<Label>{t('talentReviews.v2.sidebar.card.successors')}</Label>
			<FlexGap mt={1} alignItems="center" flexWrap="wrap" gap={tokens.gap.element}>
				{successors.map((group) => {
					const name = `${group.user.firstName} ${group.user.lastName}`;
					const tooltip = successorTooltipText(
						group.nominations.map((nomination) => ({
							readiness: t(readinessLabelKey(nomination.readiness)),
							jobTitle: nomination.jobTitle,
							motivation: QuillText.toPlainText(nomination.motivation),
						})),
						t,
					);

					const nomination = nominationToEdit(group);

					return (
						<SuccessorTooltip key={group.user.id} content={tooltip} position="top" portal>
							{nomination ? (
								<AvatarButton
									type="button"
									aria-label={name}
									onClick={() =>
										setModal({
											jobTitleID: nomination.jobTitleID,
											nomination: { ...nomination, user: group.user },
										})
									}
								>
									<Avatar size={AVATAR_SIZE} user={group.user} />
								</AvatarButton>
							) : (
								<Avatar size={AVATAR_SIZE} user={group.user} aria-label={name} />
							)}
						</SuccessorTooltip>
					);
				})}
				{nominatableRole && (
					<IconButton
						type="secondary"
						icon={<UserPlusIcon />}
						aria-label={label}
						title={label}
						onClick={() => setModal({ jobTitleID: nominatableRole.id })}
					/>
				)}
			</FlexGap>
			{modal && (
				<NominateSuccessorModal
					key={modal.nomination?.id ?? 'add'}
					jobTitleID={modal.jobTitleID}
					nomination={modal.nomination}
					onClose={() => setModal(null)}
					onNominated={() => setModal(null)}
				/>
			)}
		</Box>
	);
}
